import * as React from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import {
  CustomDateRange,
  TimeframeBar,
  daterangeUtm,
  generatePathForTimeframeDateRange,
  getDateRangeGroupsWithHref,
  getTimeframeFromDateRange,
  timeframeUtm,
  useCustomDateRangeDialog,
  useFavoriteTimeframes,
} from '../../../main/components/timeframe-bar'
import { SpecificChartFunctionality, TIMEFRAME } from '../../../main/constants'
import { useTrackPageView } from '../../../main/hooks/use-track-pageview'
import { Instrument } from '../../../main/types'
import { StockDetailTimeframeBar, getTimeframeGroupsWithHref } from '../../charts-lib'
import { encodeQueryString } from '../../queryString'

const IMAGE_SELECTOR = '.charts-gal'

function updateUrlString(urlString: string, query: Record<string, string | undefined>) {
  const url = new URL(urlString)

  Object.keys(query).forEach((key: keyof typeof query) => {
    const value = query[key]
    if (value === undefined) {
      url.searchParams.delete(key)
    } else {
      url.searchParams.set(key, value)
    }
  })

  return url.toString()
}

function updateImageSrc(image: HTMLImageElement, query: Record<string, string | undefined>) {
  image.src = updateUrlString(image.src, query)

  if (image.srcset) {
    image.srcset = image.srcset
      .split(', ')
      .map((dyna) => {
        const [url, rule] = dyna.trim().split(' ')
        return `${updateUrlString(url.trim(), query)} ${rule}`
      })
      .join(', ')
  }
}

function updateLinkhref(linkElement: HTMLAnchorElement | null, query: Record<string, string | undefined>) {
  if (!linkElement) return

  linkElement.href = updateUrlString(linkElement.href, query)
}

function getPageTitle(isDetail?: boolean, pageType?: string, ticker?: string, timeframe?: string) {
  return [pageType, ticker, isDetail ? 'Chart' : 'Charts', timeframe].filter(Boolean).join(' ')
}

export function FuturesForexCryptoTimeframeBar({
  isDetail = false,
  title,
  group,
  specificChartFunctionality,
  instrument,
  onTimeframeChange,
  onDateRangeChange,
}: {
  isDetail?: boolean
  title: string
  group?: string
  instrument: Instrument
  specificChartFunctionality: SpecificChartFunctionality
  onTimeframeChange?: (value: TIMEFRAME) => void
  onDateRangeChange?: (value: string | undefined) => void
}) {
  useTrackPageView({ trackAutomatically: !isDetail })
  const isPremium = true // Force show all timeframes for all users
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const query = React.useMemo(() => Object.fromEntries(searchParams) as { p: string; r?: string }, [searchParams])
  const { favoriteTimeframes, toggleFavoriteTimeframe } = useFavoriteTimeframes()
  const dateRange = query.r ?? undefined
  const { timeFrameGroups, dateRangeGroupsWithHref } = React.useMemo(() => {
    const timeFrameGroups = getTimeframeGroupsWithHref({
      isPremium,
      generatePath: (timeFrame) =>
        generatePathForTimeframeDateRange({
          isPremium,
          pathname: location.pathname,
          timeframe: timeFrame.value,
          dateRange: null,
          utmCampaign: timeframeUtm[specificChartFunctionality],
        }),
    })
    const dateRangeGroupsWithHref = getDateRangeGroupsWithHref({
      isPremium,
      customRange: dateRange,
      isCustomAvailable: true,
      instrument,
      generatePath: (dateRange) =>
        generatePathForTimeframeDateRange({
          isPremium,
          pathname: window.location.pathname,
          timeframe: dateRange.timeframe!,
          dateRange: dateRange.value,
          utmCampaign: daterangeUtm[specificChartFunctionality],
        }),
    })

    return { timeFrameGroups, dateRangeGroupsWithHref }
  }, [isPremium, location.pathname, dateRange, instrument, specificChartFunctionality])

  const { onDialogSubmit, customDateRangeDialogState, handleDateRangeSelect } = useCustomDateRangeDialog({
    instrument,
    anchorDate: new Date(),
    shouldReloadOnNavigate: false,
    pathGenerator: ({ dateRange, timeframe }) => ({
      pathname: window.location.pathname,
      search: new URLSearchParams({ ...query, p: timeframe, r: dateRange ?? '' }).toString(),
    }),
  })

  const handleTimeframeDateRangeChange = ({
    timeframe,
    dateRange,
  }: {
    timeframe: TIMEFRAME
    dateRange: string | undefined
  }) => {
    document.querySelectorAll<HTMLImageElement>(IMAGE_SELECTOR).forEach((image) => {
      updateImageSrc(image, { tf: timeframe, r: dateRange })
      updateLinkhref(image.closest('a'), { p: timeframe, r: dateRange })
    })
    onTimeframeChange?.(timeframe)
    onDateRangeChange?.(dateRange)
  }

  if (isDetail) {
    return (
      <StockDetailTimeframeBar
        className="justify-end space-x-2"
        isPremium={isPremium}
        isDateRangeAvailable
        isScrollIntoViewEnabled={true}
        onTimeframeSelect={(timeframe) => {
          document.title = getPageTitle(isDetail, title, group, timeframe.labelPageTitle ?? timeframe.label)
          onTimeframeChange?.(timeframe.value)
        }}
        onDateRangeSelect={(option) => {
          if (option) {
            document.title = getPageTitle(isDetail, title, group, option.label)
          }
          onDateRangeChange?.(option.value)
        }}
      />
    )
  }

  return (
    <>
      <TimeframeBar
        isPremium={isPremium}
        className="justify-end space-x-2"
        containerElement={document.body}
        favoriteTimeframes={favoriteTimeframes}
        onFavoriteTimeframeToggle={(timeframe) => toggleFavoriteTimeframe(timeframe.value)}
        selectedTimeframe={query.p ?? TIMEFRAME.d}
        timeFrameGroups={timeFrameGroups}
        onTimeframeSelect={(option) => {
          if (option.value === 'intraday') {
            // intraday option is only available for free users, and it redirects them to elite page
            return
          }
          document.title = getPageTitle(isDetail, title, group, option.labelPageTitle ?? option.label)
          navigate(`${location.pathname}?${encodeQueryString({ ...query, p: option.value })}`)
          handleTimeframeDateRangeChange({ timeframe: option.value, dateRange: undefined })
        }}
        selectedDateRange={query['r'] ?? null}
        dateRangeGroups={dateRangeGroupsWithHref}
        onDateRangeSelect={(option) => {
          handleDateRangeSelect(option, ({ timeframe, value: dateRange }) => {
            document.title = getPageTitle(isDetail, title, group, option.label)
            handleTimeframeDateRangeChange({ timeframe, dateRange })
          })
        }}
        onCustomDateRangeClick={() => {
          if (isPremium) customDateRangeDialogState.show()
        }}
      />
      <CustomDateRange
        dialogState={customDateRangeDialogState}
        dateRange={dateRange}
        anchorDate={new Date()}
        onDialogSubmit={({ dateRange }: { dateRange: string | null }) => {
          const value = dateRange ?? ''
          const timeframe = getTimeframeFromDateRange({
            dateRange: value,
            instrument: Instrument.Stock,
            anchorDate: new Date(),
            isPremium: isPremium,
          })
          handleTimeframeDateRangeChange({ timeframe, dateRange: value || undefined })
          return onDialogSubmit({ value, timeframe })
        }}
      />
    </>
  )
}
